import React from "react";

// =================== ROUTER ===================
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useOutletContext,
} from "react-router-dom";

// =================== CONTEXT PROVIDERS ===================
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";

// =================== ROUTE GUARDS ===================
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";

// =================== LAYOUTS ===================
import AppLayout from "./AppLayout";

// =================== PAGES ===================
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import NewsPage from "./pages/NewsPage";
import CommunityPage from "./pages/CommunityPage";
import SettingsPage from "./pages/SettingsPage";
import ProfilePage from "./pages/ProfilePage";
import ProfileSettingsPage from "./pages/ProfileSettingsPage";
import WatchlistPage from "./pages/WatchlistPage";
import NotFoundPage from "./pages/NotFoundPage";
import MessagePage from "./pages/MessagePage";
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import InformatrixPage from "./pages/InformatrixPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import VerifySubscriptionPage from "./pages/VerifySubscriptionPage";
import AdminPage from "./pages/AdminPage";
import NotificationsPage from "./pages/NotificationsPage";
import MarketsPage from "./pages/MarketsPage";

// --- NEW IMPORTS ---
import TermsAndConditions from './components/Legal/TermsAndConditions';
import PrivacyPolicy from './components/Legal/PrivacyPolicy';

// =================== LOADING COMPONENT ===================
import AppLoadingSpinner from "./components/AppLoadingSpinner";

// =================== ADMIN GATEKEEPER ===================
function AdminRedirectGate() {
  const { currentUser } = useAuth();
  if (currentUser?.is_admin) {
    return <Navigate to="/dashboard/admin" replace />;
  }
  return <Outlet />;
}

function MarketsAccessGate() {
  const { marketAccess, marketAccessLoading } = useOutletContext();
  if (marketAccessLoading) return <AppLoadingSpinner />;
  return marketAccess ? <MarketsPage /> : <Navigate to="/dashboard" replace />;
}

// =================== APP CONTENT COMPONENT ===================
function AppContent() {
  if (process.env.NODE_ENV === 'production') {
    console.log = () => {};
    console.error = () => {};
    console.warn = () => {};
  }
  
  const { initialized } = useAuth();

  if (!initialized) {
    return <AppLoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <Routes>
        {/* Root Redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />



        {/* =========================================================
            PUBLIC ROUTES (Accessible without Login)
            ========================================================= */}
        <Route element={<PublicRoute />}>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Route>

        {/* --- MOVED LEGAL ROUTES HERE (PUBLIC, STANDALONE) --- */}
        {/* These match the links in AuthPage: /terms and /privacy */}
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />



        {/* =========================================================
            PROTECTED ROUTES (Login Required)
            ========================================================= */}
        <Route element={<ProtectedRoute />}>
          
          {/* 1. Admin Panel (Highest Priority, Standalone Layout) */}
          <Route path="/dashboard/admin" element={<AdminPage />} />
          
          {/* 2. Subscription Verification (Standalone) */}
          <Route path="/dashboard/subscription/verify" element={<VerifySubscriptionPage />} />

          {/* 3. Standard User Routes (Wrapped in AppLayout) */}
          <Route element={<AdminRedirectGate />}>
            <Route path="/dashboard" element={<AppLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="news" element={<NewsPage />} />
              <Route path="community" element={<CommunityPage />} />
              <Route path="messages/:userId" element={<MessagePage />} />
              <Route path="messages" element={<MessagePage />} />
              <Route path="watchlist" element={<WatchlistPage />} />
              <Route path="markets" element={<MarketsAccessGate />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="settings/profile" element={<ProfileSettingsPage />} />
              <Route path="profile/:userId" element={<ProfilePage />} />
              <Route path="subscription" element={<SubscriptionPage />} />
              <Route path="settings/informatrix" element={<InformatrixPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
            </Route>
          </Route>

        </Route>

        {/* 404 Catch-All */}
        <Route path="*" element={<NotFoundPage />} />
        
      </Routes>
    </div>
  );
}

// =================== MAIN APP COMPONENT ===================
export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
